import React from 'react';
import { Box, Typography, Container, Grid, Paper, Stack } from '@mui/material';
import { styled, keyframes } from '@mui/system';
import AndroidIcon from '@mui/icons-material/Android';
import LayersIcon from '@mui/icons-material/Layers';
import LanguageIcon from '@mui/icons-material/Language';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import LocationOnIcon from '@mui/icons-material/LocationOn';


const floatAnimation = keyframes`
  0% { transform: perspective(1000px) translateY(0px) rotateX(0deg) rotateY(0deg) scale(1); }
  33% { transform: perspective(1000px) translateY(-12px) rotateX(4deg) rotateY(-2deg) scale(1.02); }
  66% { transform: perspective(1000px) translateY(-6px) rotateX(-2deg) rotateY(4deg) scale(1.01); }
  100% { transform: perspective(1000px) translateY(0px) rotateX(0deg) rotateY(0deg) scale(1); }
`;

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: '24px',
  height: '100%',
  width: '30vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: '24px',
  background: 'rgba(255, 255, 255, 0.05)',
  backdropFilter: 'blur(15px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  color: '#fff',
  transition: 'all 0.4s ease',
  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  boxSizing: 'border-box',
  '&:hover': {
    transform: 'scale(1.03) translateY(-10px)',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid #1976d2',
    boxShadow: '0 20px 40px rgba(25, 118, 210, 0.3)',
  }
}));

const IntroSection = () => {
  const expertise = [
    { icon: <AndroidIcon sx={{ fontSize: 35, color: '#3DDC84' }} />, title: 'Native Android', desc: 'Expertise in building high-performance apps using Java, Kotlin, and Jetpack Compose.' },
    { icon: <LayersIcon sx={{ fontSize: 35, color: '#64b5f6' }} />, title: 'Hybrid Apps', desc: 'Developing seamless cross-platform experiences with Flutter and React-Native.' },
    { icon: <LanguageIcon sx={{ fontSize: 35, color: '#ff9800' }} />, title: 'Web Dev', desc: 'Crafting modern, responsive websites and admin dashboards with professional UX.' },
    { icon: <BluetoothIcon sx={{ fontSize: 35, color: '#1976d2' }} />, title: 'Connectivity', desc: 'Specialized in Bluetooth, RFID, and barcode-based industrial solutions.' },
    { icon: <LocationOnIcon sx={{ fontSize: 35, color: '#f44336' }} />, title: 'Location Services', desc: 'Implementing real-time Geo-tracking, direction featuring, and Google Maps integration.' }
  ];

  return (
    <Box sx={{
      minHeight: '80vh',
      display: 'flex',
      alignItems: 'center',
      background: 'radial-gradient(circle at 80% 50%, #1e293b 0%, #0f172a 100%)',
      overflow: 'hidden',
      position: 'relative',
      py: 10
    }}>
      {/* Decorative Blur Circle */}
      <Box sx={{
        position: 'absolute',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(25, 118, 210, 0.15) 0%, transparent 70%)',
        bottom: '-10%',
        left: '-5%',
        zIndex: 1
      }} />

      <Container maxWidth="lg" sx={{ zIndex: 2 }}>
        <Grid container spacing={8} alignItems="center" gridRow={2}>

          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Typography variant="overline" sx={{ color: '#64b5f6', fontWeight: 800, letterSpacing: 4, fontSize: '1rem' }}>
                ABOUT ME
              </Typography>

              <Typography variant="h2" sx={{
                fontWeight: 900, color: '#fff',
                fontSize: { xs: '2.5rem', md: '3.8rem' },
                lineHeight: 1.1,
                textShadow: '0 10px 20px rgba(0,0,0,0.3)'
              }}>
                Innovating Through <br />
                <span style={{ color: '#1976d2' }}>Lines of Code</span>
                <Box sx={{ height: 4, width: 60, bgcolor: '#3b82f6', mt: 3, mx: 'auto', borderRadius: 2 }} />
              </Typography>


              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.1rem', lineHeight: 1.8 }}>
                Hi, I'm <strong>Vikash Tiwari</strong>, a dedicated Software Developer with a passion for transforming complex ideas into functional, high-performance mobile applications. I specialize in <strong>Native Android (Java/Kotlin)</strong> and <strong>Cross-Platform (Flutter/ React-Native)</strong> development, focusing on creating seamless user interfaces with <strong>Jetpack Compose</strong>.
              </Typography>

              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.1rem', lineHeight: 1.8 }}>
                My expertise extends beyond standard app development; I have deep experience in integrating hardware-level functionalities like <strong>Bluetooth connectivity</strong> and <strong>External SDKs</strong> to build industrial-grade solutions. I believe in writing clean, maintainable code and am always driven by the challenge of learning new technologies to solve real-world problems.
              </Typography>

              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.8 }}>
                With a strong foundation in <strong>Bachelor of Computer Applications (BCA)</strong>, I combine technical logic with a meticulous attention to detail to ensure every project improves usability and performance.
              </Typography>
            </Stack>
          </Grid>

          {/* Parent wrapper ko flex banaya taaki sab kuch center ho jaye */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            py: 4
          }}>
            {/* Yahan md={6} ko md={8} ya md={10} kar sakte hain agar aapko cards bade chahiye */}
            <Grid item xs={12} md={8}>
              <Box sx={{ animation: `${floatAnimation} 6s ease-in-out infinite` }}>
                {/* Grid container mein justifyContent="center" add kiya hai */}
                <Grid container spacing={3} justifyContent="center" alignItems="center">
                  {expertise.map((item, index) => (
                    <Grid
                      item
                      xs={12} // Mobile pe full width
                      sm={6}  // Tablet/Desktop pe 2 cards per row
                      key={index}
                      sx={{ display: 'flex', justifyContent: 'center' }} // Card ko apne cell mein center karne ke liye
                    >
                      <InfoCard
                        elevation={0}
                        sx={{
                          width: '100%',
                          maxWidth: '400px', // Card ki width control karne ke liye
                          textAlign: 'center', // Text center karne ke liye
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center'
                        }}
                      >
                        <Box sx={{
                          p: 1.5,
                          borderRadius: '15px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          width: 'fit-content',
                          mb: 2,
                          display: 'flex',
                          justifyContent: 'center'
                        }}>
                          {item.icon}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, fontSize: '1.1rem', color: '#fff' }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                          {item.desc}
                        </Typography>
                      </InfoCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Box>


        </Grid>
      </Container>
    </Box>
  );
};

export default IntroSection;
